import type {ResponsePart, ToolCallState} from '@microsoft/agent-host-protocol'
import {extractSubagent} from '@github-ui/ahp-relay/ahp-session-selectors'
import {isTaskCompletePart} from '@github-ui/ahp-relay/task-complete'
import {DISPLAY_GITHUB_REFERENCES_TOOL_NAME} from '@github-ui/copilot-shared-ui/components/github-references'
import {ahpToolCallToToolProps, hasToolResultImages} from '../../../ahp/ahp-tool-helpers'
import {TOOL_NAMES, TOOLS_HIDDEN_FROM_TIMELINE} from '../../../constants/tool-names'
import {parseApplyPatchPaths} from '../../../utils/artifacts/apply-patch-parser'
import {isGitHubCodeSearchToolCall} from '../../../utils/code-search-display'
import {isDeniedCancellation} from './parts/AhpToolCall'

export type ResponsePartEntry = {part: ResponsePart; index: number}

export type ToolCallGroup = {
  kind: 'group'
  id: string
  items: ResponsePartEntry[]
  toolCalls: ToolCallState[]
}

export type GroupedResponsePart = ({kind: 'part'} & ResponsePartEntry) | ToolCallGroup

const STANDALONE_TOOL_NAMES = new Set([
  'ask_user',
  'offer_catalog_tool',
  'image_generation',
  'add_pr_review_comment',
  'publish_chat_update',
])

export function isResultCardToolCall(toolCall: ToolCallState): boolean {
  return (
    toolCall.toolName === 'create_pull_request' ||
    toolCall.toolName === TOOL_NAMES.PROPOSE_WORK ||
    (toolCall.status === 'completed' && toolCall.success && hasToolResultImages(toolCall.content))
  )
}

export function trailingBlockStart(
  entries: readonly ResponsePartEntry[],
  hasSteering: (index: number) => boolean,
  completionIndex?: number,
): number {
  let start = 0
  for (const [position, {part, index}] of entries.entries()) {
    if (hasSteering(index)) start = position
    if (part.kind === 'markdown' && part.content?.trim()) start = position + 1
    if (index === completionIndex) start = position + 1
  }
  const lastEntry = entries.at(-1)
  if (lastEntry && hasSteering(lastEntry.index + 1)) start = entries.length
  return start
}

export function groupResponseParts(
  entries: readonly ResponsePartEntry[],
  {
    hasSubagent,
    completionIndex,
    hasSteering,
    deferTrailingBlock = false,
  }: {
    hasSubagent: (toolCall: ToolCallState) => boolean
    completionIndex: number | undefined
    hasSteering: (index: number) => boolean
    deferTrailingBlock?: boolean
  },
): GroupedResponsePart[] {
  const result: GroupedResponsePart[] = []
  let pending: ResponsePartEntry[] = []
  let toolCalls: ToolCallState[] = []
  let lastToolIndex = -1
  const deferFrom = deferTrailingBlock ? trailingBlockStart(entries, hasSteering, completionIndex) : entries.length

  const flush = () => {
    const firstTool = toolCalls[0]
    const members = pending.slice(0, lastToolIndex + 1)
    const reasoningCount = members.filter(({part}) => part.kind === 'reasoning' && part.content?.trim()).length
    if (toolCalls.length + reasoningCount >= 2 && firstTool) {
      result.push({
        kind: 'group',
        id: `group:${firstTool.toolCallId}`,
        items: members,
        toolCalls,
      })
      result.push(...pending.slice(lastToolIndex + 1).map(entry => ({kind: 'part' as const, ...entry})))
    } else {
      result.push(...pending.map(entry => ({kind: 'part' as const, ...entry})))
    }
    pending = []
    toolCalls = []
    lastToolIndex = -1
  }

  for (const [position, entry] of entries.entries()) {
    const {part, index} = entry
    if (position >= deferFrom) {
      flush()
      result.push({kind: 'part', ...entry})
      continue
    }
    if (hasSteering(index)) flush()
    if (index === completionIndex) {
      flush()
      result.push({kind: 'part', ...entry})
      continue
    }
    if (part.kind === 'markdown' || part.kind === 'reasoning') {
      if (!part.content?.trim()) {
        pending.push(entry)
        continue
      }
      if (part.kind === 'reasoning') {
        pending.push(entry)
        continue
      }
    } else if (part.kind === 'toolCall') {
      if (isTaskCompletePart(part)) {
        pending.push(entry)
        continue
      }
      const toolCall = part.toolCall
      if (STANDALONE_TOOL_NAMES.has(toolCall.toolName)) {
        flush()
        result.push({kind: 'part', ...entry})
        continue
      }
      const subagentPresent = Boolean(extractSubagent(toolCall)) || hasSubagent(toolCall)
      if (
        TOOLS_HIDDEN_FROM_TIMELINE.has(toolCall.toolName) &&
        toolCall.toolName !== 'task' &&
        !isDeniedCancellation(toolCall) &&
        !subagentPresent
      ) {
        pending.push(entry)
        continue
      }
      if (
        toolCall.status === 'completed' &&
        !subagentPresent &&
        toolCall.toolName !== 'task' &&
        toolCall.toolName !== DISPLAY_GITHUB_REFERENCES_TOOL_NAME &&
        !isResultCardToolCall(toolCall)
      ) {
        pending.push(entry)
        toolCalls.push(toolCall)
        lastToolIndex = pending.length - 1
        continue
      }
    }
    flush()
    result.push({kind: 'part', ...entry})
  }
  flush()
  return result
}

export type ResponseRun = {
  kind: 'run'
  id: string
  items: ResponsePartEntry[]
  toolCalls: ToolCallState[]
  visible: boolean
}

export type ResponseSectionItem = (({kind: 'part'; visible: boolean} & ResponsePartEntry) | ResponseRun) & {
  hideable: boolean
}

export type ResponseSection = {
  id: string
  firstIndex: number
  items: ResponseSectionItem[]
  prefix: ResponsePartEntry[]
  toolCalls: ToolCallState[]
}

export function segmentResponseParts(
  entries: readonly ResponsePartEntry[],
  {
    isVisible,
    completionIndex,
    hasSteering,
    live,
    previousRunIds,
  }: {
    isVisible: (entry: ResponsePartEntry) => boolean
    completionIndex: number | undefined
    hasSteering: (index: number) => boolean
    live: boolean
    previousRunIds?: ReadonlyMap<number, string>
  },
): ResponseSection[] {
  const previousIds = new Set(previousRunIds?.values())
  const isProseOrCompletionAnswer = (entry: ResponsePartEntry) =>
    entry.index === completionIndex || (entry.part.kind === 'markdown' && Boolean(entry.part.content?.trim()))
  const isAnswer = (entry: ResponsePartEntry) =>
    isProseOrCompletionAnswer(entry) ||
    (entry.part.kind === 'toolCall' &&
      (entry.part.toolCall.toolName === DISPLAY_GITHUB_REFERENCES_TOOL_NAME ||
        isResultCardToolCall(entry.part.toolCall)) &&
      isVisible(entry))
  const visibleToolCalls = (items: readonly ResponsePartEntry[]) =>
    items.flatMap(entry => (entry.part.kind === 'toolCall' && isVisible(entry) ? [entry.part.toolCall] : []))

  const sectionEntries: ResponsePartEntry[][] = []
  for (const entry of entries) {
    const current = sectionEntries.at(-1)
    if (!current || hasSteering(entry.index)) sectionEntries.push([entry])
    else current.push(entry)
  }

  const sections: ResponseSection[] = []
  for (const section of sectionEntries) {
    const first = section[0]
    if (!first) continue
    let prefixEnd = 0
    if (!live) {
      prefixEnd = section.length
      let hasProseOrCompletionAnswer = false
      for (let position = section.length - 1; position >= 0; position--) {
        const entry = section[position]
        if (!entry) continue
        if (isAnswer(entry)) {
          prefixEnd = position
          if (isProseOrCompletionAnswer(entry)) hasProseOrCompletionAnswer = true
        } else if (isVisible(entry) && (entry.part.kind !== 'reasoning' || hasProseOrCompletionAnswer)) break
      }
    }

    const items: ResponseSectionItem[] = []
    const claimedIds = new Set<string>()
    let run: ResponsePartEntry[] = []
    let runStart = 0
    const runId = (members: readonly ResponsePartEntry[], firstIndex: number) => {
      let id = members
        .map(entry => previousRunIds?.get(entry.index))
        .find(previous => previous !== undefined && !claimedIds.has(previous))
      if (!id) {
        id = `run:${firstIndex}`
        for (let suffix = 1; claimedIds.has(id) || previousIds.has(id); suffix++) id = `run:${firstIndex}:${suffix}`
      }
      claimedIds.add(id)
      return id
    }
    const pushRun = () => {
      const runFirst = run[0]
      if (runFirst) {
        items.push({
          kind: 'run',
          id: runId(run, runFirst.index),
          items: run,
          toolCalls: visibleToolCalls(run),
          visible: run.some(isVisible),
          hideable: runStart < prefixEnd,
        })
      }
      run = []
    }
    for (const [position, entry] of section.entries()) {
      if (isAnswer(entry)) {
        pushRun()
        items.push({kind: 'part', ...entry, visible: isVisible(entry), hideable: position < prefixEnd})
      } else {
        if (run.length === 0) runStart = position
        run.push(entry)
      }
    }
    pushRun()

    const prefix = section.slice(0, prefixEnd)
    sections.push({
      id: `section:${first.index}`,
      firstIndex: first.index,
      items,
      prefix,
      toolCalls: visibleToolCalls(prefix),
    })
  }
  return sections
}

const FILE_CHANGE_TOOL_NAMES = new Set(['edit', 'str_replace', 'insert', 'undo_edit', 'create', 'apply_patch'])

export function isFileChangeToolCall(toolCall: ToolCallState): boolean {
  if (FILE_CHANGE_TOOL_NAMES.has(toolCall.toolName)) return true
  if (toolCall.toolName !== 'str_replace_editor') return false
  try {
    return stringArg(cachedToolProps(toolCall).args, 'command') !== 'view'
  } catch {
    return false
  }
}

const SILENT_TOOL_NAMES = new Set([
  'task',
  'task_complete',
  'read_agent',
  'annotate_diff_line',
  'send_session_message',
  'create_session',
  'create_pull_request',
  'create_issue',
  'get_changes_overview',
  'read_bash',
  'read_powershell',
  'stop_bash',
  'stop_powershell',
  'write_bash',
  'write_powershell',
  'list_bash',
  'list_powershell',
  'clear_widget',
  'discover_widgets',
  'exit_plan_mode',
  'rename_session',
  'rename_workspace',
  'rename_branch',
  'sql',
  'session_store_sql',
  'store_memory',
  'navigate_to',
  'list_sessions_and_chats',
  'list_workspaces_and_chats',
  'list_items',
  'list_projects',
  'list_agents',
  'get_session',
  'respond_to_session_plan',
  'respond_to_session_plan_v2',
  'get_workspace',
  'delete_item',
  'suggest_items',
  'tool_search_tool_regex',
  'search_tool_catalog',
  'offer_catalog_tool',
  'report_intent',
])

function stringArg(args: unknown, key: string): string {
  if (args === null || typeof args !== 'object') return ''
  const value: unknown = Reflect.get(args, key)
  return typeof value === 'string' ? value.trim() : ''
}

function basename(path: string): string {
  return (
    path
      .replace(/[\\/]+$/, '')
      .split(/[\\/]/)
      .pop() ?? path
  )
}

const DOT_PREFIX_DIRECTORIES = new Set([
  '.',
  '..',
  '.github',
  '.vscode',
  '.git',
  '.next',
  '.cache',
  '.idea',
  '.husky',
  '.cargo',
  '.claude',
  '.codex',
  '.config',
  '.ssh',
  '.storybook',
])

function isLikelyFile(path: string): boolean {
  if (/[\\/]$/.test(path)) return false
  const name = basename(path)
  if (DOT_PREFIX_DIRECTORIES.has(name)) return false
  return (
    name.includes('.') ||
    /^(?:readme|license|licence|makefile|dockerfile|gemfile|rakefile|procfile|codeowners)$/i.test(name)
  )
}

function filesFragment(verb: string, paths: Set<string>, directory = false): string {
  const names = [...paths].map(basename)
  if (names.length === 1) return `${verb} ${names[0]}`
  if (names.length === 2) return `${verb} ${names[0]} and ${names[1]}`
  const remaining = names.length - 2
  const unit = directory ? (remaining === 1 ? 'directory' : 'directories') : remaining === 1 ? 'file' : 'files'
  return `${verb} ${names[0]}, ${names[1]}, and ${remaining} more ${unit}`
}

function previewCommand(command: string): string {
  const stripped = command.replace(/^\s*cd\s+(?:"[^"]*"|'[^']*'|[^\s;&]+)\s*&&\s*/, '')
  const firstLine = stripped.split('\n')[0]?.trim() ?? ''
  return firstLine.length <= 60 ? firstLine : `${firstLine.slice(0, 60)}\u2026`
}

const toolPropsCache = new WeakMap<ToolCallState, ReturnType<typeof ahpToolCallToToolProps>>()

function cachedToolProps(toolCall: ToolCallState) {
  let props = toolPropsCache.get(toolCall)
  if (!props) {
    props = ahpToolCallToToolProps(toolCall)
    toolPropsCache.set(toolCall, props)
  }
  return props
}

function isFailedToolCall(toolCall: ToolCallState): boolean {
  return toolCall.status === 'completed' && toolCall.success === false
}

function toolCallCount(count: number): string {
  return `${count} tool call${count === 1 ? '' : 's'}`
}

export function summarizeToolCallGroup(toolCalls: ToolCallState[]): string {
  const succeeded = toolCalls.filter(toolCall => !isFailedToolCall(toolCall))
  const failedCount = toolCalls.length - succeeded.length
  if (failedCount === 0) return summarizeSucceededToolCalls(toolCalls)
  if (succeeded.length === 0) return `${toolCallCount(failedCount)} failed`
  return `${summarizeSucceededToolCalls(succeeded)}, ${failedCount} failed`
}

function summarizeSucceededToolCalls(toolCalls: ToolCallState[]): string {
  const edits = new Set<string>()
  const creates = new Set<string>()
  const reads = new Set<string>()
  const directories = new Set<string>()
  const unknownTools = new Set<string>()
  let editCount = 0
  let createCount = 0
  let readCount = 0
  let directoryCount = 0
  let unknownToolCount = 0
  let bashLabel: string | undefined
  let searchCount = 0
  let webFetchCount = 0
  let webSearchCount = 0
  let skillCount = 0
  let askUserCount = 0
  let updatedPlanCount = 0

  for (const toolCall of toolCalls) {
    const unknownLabel = toolCall.displayName || toolCall.toolName
    try {
      const {name, args, isMcpTool} = cachedToolProps(toolCall)
      const path = stringArg(args, 'path') || stringArg(args, 'file_path') || stringArg(args, 'filename')
      switch (name) {
        case 'bash':
        case 'powershell':
        case 'local_shell':
        case 'async_bash': {
          const command = stringArg(args, 'command')
          if (command && bashLabel === undefined) {
            const description = stringArg(args, 'description')
            bashLabel = description
              ? description.charAt(0).toUpperCase() + description.slice(1)
              : `Ran ${previewCommand(command)}`
          }
          break
        }
        case 'view':
        case 'show_file':
          if (path) {
            if (isLikelyFile(path)) {
              reads.add(path)
              readCount++
            } else {
              directories.add(path)
              directoryCount++
            }
          }
          break
        case 'edit':
        case 'str_replace':
        case 'insert':
        case 'undo_edit':
          if (path) {
            edits.add(path)
            editCount++
          }
          break
        case 'create':
          if (path) {
            creates.add(path)
            createCount++
          }
          break
        case 'str_replace_editor': {
          const command = stringArg(args, 'command')
          if (path) {
            if (command === 'view') {
              reads.add(path)
              readCount++
            } else if (command === 'create') {
              creates.add(path)
              createCount++
            } else {
              edits.add(path)
              editCount++
            }
          }
          break
        }
        case 'apply_patch': {
          const patch =
            typeof args === 'string'
              ? args
              : stringArg(args, 'input') ||
                stringArg(args, 'arguments') ||
                stringArg(args, 'patch') ||
                stringArg(args, 'content')
          const paths = parseApplyPatchPaths(patch)
          if (paths.length === 0) {
            unknownTools.add(unknownLabel)
            unknownToolCount++
          } else {
            editCount++
          }
          for (const write of paths) edits.add(write.path)
          break
        }
        case 'grep':
        case 'rg':
        case 'glob':
        case 'search_code_subagent':
        case 'github-mcp-server-search_code':
          searchCount++
          break
        case 'lexical_code_search':
        case 'semantic_code_search':
          if (isGitHubCodeSearchToolCall({name, isMcpTool})) searchCount++
          else {
            unknownTools.add(unknownLabel)
            unknownToolCount++
          }
          break
        case 'web_fetch':
          webFetchCount++
          break
        case 'web_search':
        case 'github-mcp-server-web_search':
        case 'github-mcp-server-bing_search':
        case 'bing':
          webSearchCount++
          break
        case 'skill':
          skillCount++
          break
        case 'ask_user':
          askUserCount++
          break
        case 'update_todo':
          updatedPlanCount++
          break
        case 'update_profile':
          unknownTools.add('Profile update')
          unknownToolCount++
          break
        case 'update_agent_memory':
          unknownTools.add('Memory update')
          unknownToolCount++
          break
        case 'write_agent':
          unknownTools.add('Agent messages')
          unknownToolCount++
          break
        default:
          if (name && !SILENT_TOOL_NAMES.has(name)) {
            unknownTools.add(unknownLabel)
            unknownToolCount++
          }
      }
    } catch {
      unknownTools.add(unknownLabel)
      unknownToolCount++
    }
  }

  if (bashLabel !== undefined) {
    const remaining = toolCalls.length - 1
    return remaining === 0 ? bashLabel : `${bashLabel} and ${remaining} other tool call${remaining === 1 ? '' : 's'}`
  }

  const fragments: Array<[string, number]> = []
  if (edits.size) fragments.push([filesFragment('Edited', edits), editCount])
  if (creates.size) fragments.push([filesFragment('Created', creates), createCount])
  if (reads.size) fragments.push([filesFragment('Read', reads), readCount])
  if (directories.size) fragments.push([filesFragment('Listed', directories, true), directoryCount])
  for (const [count, verb, singular, plural] of [
    [searchCount, 'Searched', 'query', 'queries'],
    [webFetchCount, 'Fetched', 'page', 'pages'],
    [webSearchCount, 'Searched the web', 'query', 'queries'],
    [skillCount, 'Ran', 'skill', 'skills'],
    [askUserCount, 'Asked', 'question', 'questions'],
  ] as const) {
    if (count > 0) fragments.push([`${verb} ${count} ${count === 1 ? singular : plural}`, count])
  }
  if (updatedPlanCount) fragments.push(['Updated plan', updatedPlanCount])
  if (unknownTools.size) fragments.push([[...unknownTools].join(', '), unknownToolCount])
  const firstFragment = fragments[0]
  if (!firstFragment) return `Used ${toolCalls.length} tool${toolCalls.length === 1 ? '' : 's'}`
  const [label, count] = firstFragment
  const remaining = toolCalls.length - count
  return remaining === 0 ? label : `${label} and ${remaining} other tool call${remaining === 1 ? '' : 's'}`
}
